import classNames from "classnames";
import { FormHelperText, FormNumericInput, FormSwitch, FormTextArea } from "components/form-fields";
import { ShowHide } from "components/layout";
import { Tooltip } from "components/tooltip";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";
import React, { Fragment, useEffect } from "react";

export function TemplateSettings(
    {
        isReadOnly = false,
        isNew = true,
        category = "",
        loading,
        onChange,
        onValidationChange,
        saving,
        template,
        templateOptions
    }
) {
    const [isValid, errors, validate] = useValidation();

    useEffect(() => {
        setDefaultsForSubCategory()

        const rules = [];

        if (template.signatureRequiredWhenCompletingCheck) {
            rules.push({ fieldName: "textToDisplayWithSignature", required: true, maxLength: 1000 });
        }

        if (template.allowManagerSignOff) {
            rules.push({ fieldName: "managerSignOffTextToDisplay", required: true, maxLength: 1000 });
            rules.push({ fieldName: "managerSignOffIsOnlyRequiredOnFailedChecks", required: true });
        }

        validate(rules, template);
    }, [template, category, isNew]);

    useEffect(() => onValidationChange(isValid), [isValid]);

    useEffect(() => {
        if (template.allowManagerSignOff
            && !template.managerSignOffIsOnlyRequiredOnFailedChecks
            && isNew
        ) {
            setTemplateValue(true, "managerSignOffIsOnlyRequiredOnFailedChecks");
        }
    }, [template?.allowManagerSignOff]);

    const setDefaultsForSubCategory = () => {
        if (!isNew || !category) {
            return;
        }

        const subCategory = getSubCategory();
        let valueChanged = false;
        const templateClone = { ...template }

        if (subCategory.allowManagerSignOff != null && template.allowManagerSignOff !== subCategory.allowManagerSignOff) {
            valueChanged = true;
            templateClone.allowManagerSignOff = subCategory.allowManagerSignOff;

            if (templateClone.allowManagerSignOff) {
                templateClone.managerSignOffIsOnlyRequiredOnFailedChecks = false;
            }

            if (templateClone.allowManagerSignOff && subCategory.managerSignOffTextToDisplay && !templateClone.managerSignOffTextToDisplay) {
                templateClone.managerSignOffTextToDisplay = subCategory.managerSignOffTextToDisplay;
            }
        }

        if (subCategory.requirePassFail != null && template.requirePassFailResult !== subCategory.requirePassFail) {
            valueChanged = true;
            templateClone.requirePassFailResult = subCategory.requirePassFail;
        }

        if (valueChanged) {
            onChange(templateClone);
        }
    }

    const setTemplateValue = (value, fieldName, defaultToZero) => {
        const clone = {
            ...template,
        };

        if (defaultToZero && !value) {
            value = 0;
        }

        clone[fieldName] = value;
        onChange(clone);
    }

    const setSignatureSignOffValue = (value) => {
        const clone = {
            ...template,
        };

        if (!value) {
            clone.textToDisplayWithSignature = "";
        }

        clone.signatureRequiredWhenCompletingCheck = value;
        onChange(clone);
    }

    const setManagerSignOffValue = (value) => {
        const clone = {
            ...template,
        };

        const subCategory = getSubCategory();

        if (!value) {
            clone.managerSignOffTextToDisplay = "";
            clone.managerSignOffIsOnlyRequiredOnFailedChecks = null;
        } else {
            clone.managerSignOffIsOnlyRequiredOnFailedChecks = false;
        }

        // Set the manager sign off text to display if we have enabled manager sign off 
        // and a default exists for the subcategory
        if (value && subCategory.managerSignOffTextToDisplay && !template.managerSignOffTextToDisplay) {
            clone.managerSignOffTextToDisplay = subCategory.managerSignOffTextToDisplay;
        }

        clone.allowManagerSignOff = value;
        onChange(clone);
    }

    const getSubCategory = () => {
        if (!category) {
            return null;
        }

        const subCategories = templateOptions.templateCategories.find(c => c.key === category)
            .subCategories;
        return subCategories.find(s => s.key === template.subCategory);
    };

    const renderAssetSettings = () => {
        return (
            <Fragment>
                <FormSwitch
                    id="require-odo-readings-switch"
                    headingText="Require an ODO reading when completing the check"
                    checked={template.requireOdoReadings}
                    disabled={isReadOnly || saving}
                    loading={loading}
                    onChange={(value) => setTemplateValue(value.target.checked, "requireOdoReadings")}
                    showLabels={true}
                />
                <ShowHide
                    evaluator={template.subCategory === "WalkaroundCheck"}
                    show={<FormNumericInput
                        id="image-check-percentage"
                        min={0}
                        max={100}
                        headingText="Image check percentage"
                        helperText="Percentage of questions that require an image"
                        onValueChange={(value) => setTemplateValue(value, "percentageOfChecksThatRequireRandomImage", true)}
                        selectedValue={template.percentageOfChecksThatRequireRandomImage}
                        loading={loading}
                        disabled={isReadOnly || saving}
                        disabledInput={isReadOnly || saving}
                        allowDecimal={false}
                        dangerHelperText={errors["percentageOfChecksThatRequireRandomImage"]}
                    />}
                />
            </Fragment>
        );
    }

    return (
        <div className={classNames({ "row": !isNew })}>
            <h2>Settings</h2>
            <div>
                <h4>Minimum completion time</h4>
                <FormHelperText>
                    If this field is zero then no minimum time will be
                    applied
                </FormHelperText>
                <div className="inline-items spacer-bottom">
                    <FormNumericInput
                        id="minimum-completion-time"
                        min={0}
                        max={100000}
                        selectedValue={template.minimumTimeRequiredToFillOutCheckInMinutes ? template.minimumTimeRequiredToFillOutCheckInMinutes : 0}
                        onValueChange={(value) => setTemplateValue(value, "minimumTimeRequiredToFillOutCheckInMinutes", true)}
                        allowDecimal={false}
                        disabled={isReadOnly || saving}
                        disabledInput={isReadOnly || saving}
                        dangerHelperText={errors["minimumTimeRequiredToFillOutCheckInMinutes"]}
                    />
                    <span>minutes with a grace period of</span>
                    <FormNumericInput
                        id="grace-period"
                        min={0}
                        max={template.minimumTimeRequiredToFillOutCheckInMinutes ? template.minimumTimeRequiredToFillOutCheckInMinutes : 0}
                        selectedValue={template.timeThresholdInMinutes}
                        onValueChange={(value) => setTemplateValue(value, "timeThresholdInMinutes", true)}
                        allowDecimal={false}
                        loading={loading}
                        disabled={isReadOnly || saving || !template.minimumTimeRequiredToFillOutCheckInMinutes}
                        disabledInput={isReadOnly || saving || !template.minimumTimeRequiredToFillOutCheckInMinutes}
                        dangerHelperText={errors["timeThresholdInMinutes"]}
                    />
                    <span>minutes.</span>
                </div>

                <ShowHide
                    evaluator={category === "Asset"}
                    show={renderAssetSettings()}
                />

                <Tooltip
                    disabled={getSubCategory()?.requirePassFail == null}
                    content={"The sub-category enforces a value for this setting."}>
                    <FormSwitch
                        id="pass-fail-switch"
                        headingText="Enable pass/fail for completed checks"
                        checked={template.requirePassFailResult}
                        disabled={isReadOnly || saving || getSubCategory()?.requirePassFail != null}
                        loading={loading}
                        onChange={(value) => setTemplateValue(value.target.checked, "requirePassFailResult")}
                        showLabels={true}
                    />
                </Tooltip>
                <FormSwitch
                    id="diplay-question-numbers-switch"
                    headingText="Allow population of previous results when completing check"
                    helperText="Enabling this option will allow users to copy the previous checks results when completing a new check"
                    checked={template.allowPopulationOfPreviousResults}
                    disabled={isReadOnly || saving}
                    loading={loading}
                    onChange={(value) => setTemplateValue(value.target.checked, "allowPopulationOfPreviousResults")}
                    showLabels={true}
                />
                <FormSwitch
                    id="signature-signoff-switch"
                    headingText="Enable signature sign off"
                    checked={template.signatureRequiredWhenCompletingCheck}
                    disabled={isReadOnly || saving}
                    loading={loading}
                    onChange={(value) => setSignatureSignOffValue(value.target.checked)}
                    showLabels={true}
                />

                <ShowHide
                    evaluator={template.signatureRequiredWhenCompletingCheck}
                    show={<FormTextArea
                        id="signature-text"
                        headingText="Signature sign off text"
                        value={template.textToDisplayWithSignature}
                        onChange={(e) => setTemplateValue(e.target.value, "textToDisplayWithSignature")}
                        maxLength={1000}
                        showCounter
                        dangerHelperText={errors["textToDisplayWithSignature"]}
                    />}
                />

                <Tooltip
                    disabled={getSubCategory()?.allowManagerSignOff == null}
                    content={"The sub-category enforces a value for this setting."}>
                    <FormSwitch
                        id="manager-signoff-switch"
                        headingText="Enable manager sign off"
                        checked={template.allowManagerSignOff}
                        disabled={isReadOnly || saving || getSubCategory()?.allowManagerSignOff != null}
                        loading={loading}
                        onChange={(value) => setManagerSignOffValue(value.target.checked)}
                        showLabels={true}
                    />
                </Tooltip>

                <ShowHide
                    evaluator={template.allowManagerSignOff}
                    show={<Fragment>
                        <FormSwitch
                            id="manager-signoff-failed-checks-switch"
                            headingText="Manager sign off is only required on failed checks"
                            helperText="If this option is disabled, then all checks will need a manager to sign them off, regardless of whether they passed or failed."
                            checked={template.managerSignOffIsOnlyRequiredOnFailedChecks}
                            disabled={isReadOnly || saving}
                            loading={loading}
                            onChange={(value) => setTemplateValue(value.target.checked, "managerSignOffIsOnlyRequiredOnFailedChecks")}
                            showLabels={true}
                        />
                        <FormTextArea
                            id="manager-sign-off-text"
                            headingText="Manager sign off text"
                            value={template.managerSignOffTextToDisplay}
                            onChange={(e) => setTemplateValue(e.target.value, "managerSignOffTextToDisplay")}
                            maxLength={1000}
                            showCounter
                            disabled={isReadOnly || saving}
                            dangerHelperText={errors["managerSignOffTextToDisplay"]}
                        />
                    </Fragment>}
                />
            </div>
        </div>
    );
}

TemplateSettings.propTypes = {
    template: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired,
    onValidationChange: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
    isNew: PropTypes.bool,
    category: PropTypes.string,
    templateOptions: PropTypes.object.isRequired
};